import { UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { PrenatalIntakeFormData } from "../PrenatalIntakeFormSchema";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";

interface PregnancyHistorySectionProps {
  form: UseFormReturn<PrenatalIntakeFormData>;
}

export function PregnancyHistorySection({ form }: PregnancyHistorySectionProps) {
  const isFirstPregnancy = form.watch("is_first_pregnancy");

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold border-b pb-2">Section III: Pregnancy History</h3>

      <FormField
        control={form.control}
        name="is_first_pregnancy"
        render={({ field }) => (
          <FormItem className="flex items-start space-x-3 space-y-0">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>This is my first pregnancy</FormLabel>
              <FormDescription>If checked, skip to the next section</FormDescription>
            </div>
          </FormItem>
        )}
      />

      {isFirstPregnancy && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Since this is your first pregnancy, you can proceed to the next section.
          </AlertDescription>
        </Alert>
      )}

      {!isFirstPregnancy && (
        <div className="space-y-6">
          {/* Children in Care */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="children_in_care"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Children currently in your care</FormLabel>
                  <FormDescription>Including children you have given birth to or adopted</FormDescription>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="0" 
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="living_children_born"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Living children you have given birth to</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="0" 
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Birth History */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="full_term_births"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full-term live births</FormLabel>
                  <FormDescription>Not premature delivery</FormDescription>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="0" 
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="premature_births"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Premature births (more than 3 weeks early)</FormLabel>
                  <FormDescription>* Risk factor for PNCC eligibility</FormDescription>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="0" 
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Pregnancy Loss */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="miscarriages_after_20_weeks"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Miscarriages or losses at 20 weeks or later</FormLabel>
                  <FormDescription>* Risk factor for PNCC eligibility</FormDescription>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="0" 
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="miscarriages_before_20_weeks"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Miscarriages or losses before 20 weeks</FormLabel>
                  <FormDescription>Including planned and unplanned</FormDescription>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="0" 
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Birth Weight History */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="low_birth_weight_babies"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Babies born weighing 5.5 lbs or less</FormLabel>
                  <FormDescription>* Risk factor for PNCC eligibility</FormDescription>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="0" 
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="high_birth_weight_babies"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Babies born weighing more than 9 lbs</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="0" 
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Last Pregnancy */}
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium">Last Pregnancy</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="last_pregnancy_end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>When did your last pregnancy end?</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="last_pregnancy_outcome"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Outcome of last pregnancy</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className="flex flex-col space-y-1"
                      >
                        <FormItem className="flex items-center space-x-3 space-y-0">
                          <FormControl>
                            <RadioGroupItem value="live_birth" />
                          </FormControl>
                          <FormLabel className="font-normal">Live Birth</FormLabel>
                        </FormItem>
                        <FormItem className="flex items-center space-x-3 space-y-0">
                          <FormControl>
                            <RadioGroupItem value="miscarriage_or_loss" />
                          </FormControl>
                          <FormLabel className="font-normal">Miscarriage or Other Loss</FormLabel>
                        </FormItem>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
